import React, { useState } from 'react';
import { format, isToday, isSameDay, addDays, subDays, getDay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Notes from './Notes';

const DayView = ({ currentDate, events, keyTimes, onEventClick, onNewEvent, onDateChange }) => {
  // Use the actual school day times from keyTimes
  const schoolDayTimes = keyTimes.map(timeSlot => timeSlot.time);
  
  // Weekend toggle state (exclude weekends by default)
  const [excludeWeekends, setExcludeWeekends] = useState(true);

  // Helper function to check if a date is a weekend
  const isWeekend = (date) => {
    const dayOfWeek = getDay(date);
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  };

  // Helper function to find the next/previous weekday
  const findNextWeekday = (date, direction = 1) => {
    let newDate = direction > 0 ? addDays(date, 1) : subDays(date, 1);
    while (isWeekend(newDate)) {
      newDate = direction > 0 ? addDays(newDate, 1) : subDays(newDate, 1);
    }
    return newDate;
  };

  const handlePreviousDay = () => {
    if (excludeWeekends) {
      onDateChange(findNextWeekday(currentDate, -1));
    } else {
      onDateChange(subDays(currentDate, 1));
    }
  };

  const handleNextDay = () => {
    if (excludeWeekends) {
      onDateChange(findNextWeekday(currentDate, 1));
    } else {
      onDateChange(addDays(currentDate, 1));
    }
  };
  // Get events for the current day
  const getEventsForDay = () => {
    return events.filter(event => 
      isSameDay(new Date(event.date), currentDate)
    );
  };

  // Calculate which time slots each event spans
  const getEventTimeSlots = (event) => {
    if (!event.start_time || !event.end_time) return [];

    const startTime = event.start_time.substring(0, 5); // HH:MM
    const endTime = event.end_time.substring(0, 5); // HH:MM

    const startIndex = schoolDayTimes.findIndex(time => time === startTime);
    const endIndex = schoolDayTimes.findIndex(time => time === endTime);

    if (startIndex === -1 || endIndex === -1) return [];

    return schoolDayTimes.slice(startIndex, endIndex + 1);
  };

  // Group events by their starting time slot
  const getEventsForTimeSlot = (time) => {
    return dayEvents.filter(event => {
      const eventSlots = getEventTimeSlots(event);
      return eventSlots.length > 0 && eventSlots[0] === time;
    });
  };

  // Calculate the height needed for a time slot based on its events
  const getTimeSlotHeight = (time) => {
    const events = getEventsForTimeSlot(time);
    if (events.length === 0) return 80; // Default height for empty slots

    // Find the maximum duration of events starting at this time
    let maxDuration = 0;
    events.forEach(event => {
      const eventSlots = getEventTimeSlots(event);
      maxDuration = Math.max(maxDuration, eventSlots.length);
    });

    // Each time slot is 80px, with 4px margin between
    return (maxDuration * 80) + ((maxDuration - 1) * 4);
  };

  const dayEvents = getEventsForDay();

  // Function to get the next upcoming event from current moment across all days
  const getNextEvent = () => {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes for comparison

    // Get all events from today onwards
    const allUpcomingEvents = events.filter(event => {
      if (!event.start_time || !event.date) return false;
      
      // If event is today, check if it's in the future
      if (event.date === currentDate) {
        const [hours, minutes] = event.start_time.split(':').map(Number);
        const eventTime = hours * 60 + minutes;
        return eventTime >= currentTime;
      }
      
      // If event is in the future, include it
      return event.date > currentDate;
    });

    if (allUpcomingEvents.length === 0) return null;

    // Sort by date first, then by time
    return allUpcomingEvents.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      const [aHours, aMinutes] = a.start_time.split(':').map(Number);
      const [bHours, bMinutes] = b.start_time.split(':').map(Number);
      return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
    })[0];
  };

  // Function to check if current day has any remaining events
  const hasRemainingEventsToday = () => {
    if (dayEvents.length === 0) return false;
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    return dayEvents.some(event => {
      if (!event.start_time) return false;
      const [hours, minutes] = event.start_time.split(':').map(Number);
      const eventTime = hours * 60 + minutes;
      return eventTime >= currentTime;
    });
  };

  const nextEvent = getNextEvent();

  return (
    <div className="day-view-container">
      <div className="day-header">
        <div className="day-navigation">
          <button 
            className="day-nav-button"
            onClick={handlePreviousDay}
            aria-label="Previous day"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="day-title-section">
            <h2 className="day-title">
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
              {isToday(currentDate) && <span className="today-badge">Today</span>}
              {isWeekend(currentDate) && <span className="weekend-badge">Weekend</span>}
            </h2>
            <div className="day-summary">
              {nextEvent ? (
                <>Next Event Is: <strong>{nextEvent.title}</strong> at <strong>{nextEvent.start_time}</strong></>
              ) : isToday(currentDate) && !hasRemainingEventsToday() ? (
                <>No events left in this day</>
              ) : (
                <>No upcoming events today</>
              )}
            </div>
            <div className="weekend-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={excludeWeekends}
                  onChange={(e) => setExcludeWeekends(e.target.checked)}
                  className="toggle-checkbox"
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">Exclude Weekends</span>
              </label>
            </div>
          </div>
          
          <button 
            className="day-nav-button"
            onClick={handleNextDay}
            aria-label="Next day"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="day-table">
        {schoolDayTimes.map(time => {
          const events = getEventsForTimeSlot(time);
          const slotHeight = getTimeSlotHeight(time);
          
          return (
            <div key={time} className="day-table-row">
              {/* Time Column */}
              <div 
                className="day-time-cell"
                style={{ height: `${slotHeight}px` }}
              >
                {time}
              </div>
              
              {/* Events Column */}
              <div 
                className="day-events-cell"
                style={{ height: `${slotHeight}px` }}
                onClick={() => onNewEvent(currentDate)}
              >
                {events.map(event => {
                  // Determine class from event title
                  let classCode = '';
                  if (event.title) {
                    const title = event.title.toLowerCase();
                    if (title.includes('maths intervention')) {
                      classCode = 'class-intervention';
                    } else if (title.includes('7w/ma2')) {
                      classCode = 'class-7w-ma2';
                    } else if (title.includes('9w/ma2')) {
                      classCode = 'class-9w-ma2';
                    } else if (title.includes('9e/ma3b')) {
                      classCode = 'class-9e-ma3b';
                    } else if (title.includes('8e/ma1')) {
                      classCode = 'class-8e-ma1';
                    } else if (title.includes('8w/ma1')) {
                      classCode = 'class-8w-ma1';
                    }
                  }
                  
                  const eventSlots = getEventTimeSlots(event);
                  const eventHeight = (eventSlots.length * 80) + ((eventSlots.length - 1) * 4);
                  
                  return (
                    <div
                      key={event.id}
                      className={`day-event-item ${classCode}`}
                      style={{ height: `${eventHeight}px` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      title={event.title}
                    >
                      <div className="event-time">
                        {event.start_time && event.end_time && 
                          `${event.start_time.substring(0, 5)} - ${event.end_time.substring(0, 5)}`
                        }
                      </div>
                      <div className="event-title">{event.title}</div>
                      <div className="event-room">{event.room}</div>
                      {event.description && (
                        <div className="event-description">{event.description}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Daily Notes */}
      <Notes currentDate={currentDate} />
    </div>
  );
};

export default DayView;
